import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardContent, Typography, Container, Box, Alert } from '@mui/material';
import { Download as DownloadIcon, Hotel as HotelIcon } from '@mui/icons-material';
import toast from 'react-hot-toast';

import HotelVoucherNew from '../components/hotel/HotelVoucherNew';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Copyrights from '../components/Copyrights';
import Styles from '../components/Styles';

const TestVoucher = () => {
  const { t } = useTranslation();
  const translationContext = useTranslation();

  // Sample reservation data matching your API structure
  const sampleReservationData = {
    reservationInfo: {
      bookingNumber: "RC002576",
      beginDate: "2024-12-25T00:00:00",
      endDate: "2025-01-01T00:00:00",
      totalPrice: {
        amount: 1850,
        currency: "EUR"
      }
    },
    travellers: [
      {
        travellerId: "50141",
        type: 1,
        title: 1,
        name: "John",
        surname: "DOE",
        isLeader: true,
        birthDate: "1985-03-15T00:00:00",
        age: 39,
        nationality: {
          name: "Germany",
          twoLetterCode: "DE"
        },
        address: {
          email: "john.doe@example.com",
          contactPhone: {
            countryCode: "49",
            phoneNumber: "1234567890"
          }
        }
      },
      {
        travellerId: "50142",
        type: 1,
        title: 3,
        name: "Jane",
        surname: "DOE",
        isLeader: false,
        birthDate: "1987-07-22T00:00:00",
        age: 37,
        nationality: {
          name: "Germany",
          twoLetterCode: "DE"
        }
      },
      {
        travellerId: "50143",
        type: 2,
        title: 5,
        name: "Emma",
        surname: "DOE",
        isLeader: false,
        birthDate: "2016-05-10T00:00:00",
        age: 8,
        nationality: {
          name: "Germany",
          twoLetterCode: "DE"
        }
      }
    ],
    services: [
      {
        id: "1",
        name: "GRAND HOTEL PARADISE RESORT & SPA",
        isMainService: true,
        serviceDetails: {
          hotelDetail: {
            name: "GRAND HOTEL PARADISE RESORT & SPA",
            city: {
              name: "Antalya"
            },
            country: {
              name: "Turkey"
            },
            address: {
              addressLines: ["Lara Beach, Antalya, Turkey"]
            }
          },
          night: 7,
          room: "FAMILY SUITE DELUXE SEA VIEW",
          board: "ULTRA ALL INCLUSIVE"
        },
        productType: 2
      }
    ]
  };

  return (
    <>
      <Styles />
      <Header translation={translationContext.t} translationContext={translationContext} />
      <Container maxWidth="lg" sx={{ mt: 5, mb: 5 }}>
        <Card elevation={3}>
          <CardContent sx={{ p: 4 }}>
            <Box textAlign="center" mb={4}>
              <HotelIcon sx={{ fontSize: 60, color: '#007bff', mb: 2 }} />
              <Typography variant="h3" component="h1" gutterBottom color="primary">
                {t("hotel_voucher_pdf_test")}
              </Typography>
              <Typography variant="h6" color="textSecondary">
                {t("test_hotel_voucher_pdf_description")}
              </Typography>
            </Box>

            <Alert severity="info" sx={{ mb: 4 }}>
              <Typography variant="body1">
                <strong>{t("how_to_test")}:</strong> {t("click_button_to_generate")}
              </Typography>
            </Alert>
            
            <Box sx={{ mb: 4, p: 3, bgcolor: 'grey.50', borderRadius: 2 }}>
              <Typography variant="h5" gutterBottom color="primary">
                📋 {t("mock_reservation_details")}:
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                <Box>
                  <Typography variant="body1"><strong>{t("reservation_number")}:</strong> RC002576</Typography>
                  <Typography variant="body1"><strong>{t("hotel")}:</strong> Grand Hotel Paradise Resort & Spa</Typography>
                  <Typography variant="body1"><strong>{t("location")}:</strong> Antalya, Turkey</Typography>
                  <Typography variant="body1"><strong>{t("room_type")}:</strong> Family Suite Deluxe Sea View</Typography>
                  <Typography variant="body1"><strong>{t("board_type")}:</strong> Ultra All Inclusive</Typography>
                </Box>
                <Box>
                  <Typography variant="body1"><strong>{t("check_in")}:</strong> December 25, 2024</Typography>
                  <Typography variant="body1"><strong>{t("check_out")}:</strong> January 1, 2025</Typography>
                  <Typography variant="body1"><strong>{t("nights")}:</strong> 7 {t("nights")}</Typography>
                  <Typography variant="body1"><strong>{t("guests")}:</strong> John DOE, Jane DOE, Emma DOE (child)</Typography>
                  <Typography variant="body1"><strong>{t("total_price")}:</strong> €1,850</Typography>
                </Box>
              </Box>
            </Box>

            <Box textAlign="center" mb={4}>
              <Typography variant="h6" gutterBottom color="primary">
                🎯 Professional Hotel Voucher (New Design)
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                Click the button below to download a professional PDF voucher that matches your exact brand design with FluturoLire logo.
              </Typography>
              
              {/* New Professional Voucher Component */}
              <HotelVoucherNew reservationData={sampleReservationData} />
              
              <Alert severity="success" sx={{ mt: 3, textAlign: 'left' }}>
                <Typography variant="body2">
                  <strong>✨ This voucher includes:</strong><br/>
                  • FluturoLire logo from AWS S3<br/>
                  • Professional layout matching your reference design<br/>
                  • Complete booking and guest information<br/>
                  • Terms & conditions section<br/>
                  • High-quality PDF format ready for printing
                </Typography>
              </Alert>
            </Box>

            <Box sx={{ p: 3, bgcolor: 'primary.50', borderRadius: 2, border: '1px solid', borderColor: 'primary.200' }}>
              <Typography variant="h6" gutterBottom color="primary">
                🔧 {t("how_to_use_actual_functionality")}:
              </Typography>
              <Box component="ol" sx={{ pl: 2 }}>
                <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                  {t("complete_hotel_booking_instruction")}
                </Typography>
                <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                  {t("visit_payment_page_instruction")} <code>/paymentOk</code> {t("page_directly")}
                </Typography>
                <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                  {t("enter_reservation_number_instruction")} (e.g., RC002576) {t("in_search_form")}
                </Typography>
                <Typography component="li" variant="body2" sx={{ mb: 1 }}>
                  {t("click_search_instruction")}
                </Typography>
                <Typography component="li" variant="body2">
                  {t("click_download_voucher_instruction")}
                </Typography>
              </Box>
              
              <Alert severity="warning" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  <strong>{t("note")}:</strong> {t("actual_functionality_note")}
                </Typography>
              </Alert>
            </Box>

            <Box sx={{ mt: 4, p: 3, bgcolor: 'success.50', borderRadius: 2, border: '1px solid', borderColor: 'success.200' }}>
              <Typography variant="h6" gutterBottom color="success.main">
                ✨ {t("pdf_features_included")}:
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1 }}>
                <Typography variant="body2">• {t("fluturo_branding")}</Typography>
                <Typography variant="body2">• {t("professional_design_layout")}</Typography>
                <Typography variant="body2">• {t("complete_booking_information")}</Typography>
                <Typography variant="body2">• {t("guest_details_and_room_info")}</Typography>
                <Typography variant="body2">• {t("checkin_checkout_dates")}</Typography>
                <Typography variant="body2">• {t("pricing_information")}</Typography>
                <Typography variant="body2">• {t("important_travel_notes")}</Typography>
                <Typography variant="body2">• {t("high_quality_pdf_format")}</Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Container>
      <Footer />
      <Copyrights />
    </>
  );
};

export default TestVoucher; 